import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from 'react-router';
import {useDispatch, useSelector} from 'react-redux';
import {
    getQuoteById,
    getQuoteStatuses,
    getQuoteStatusesStatus, selectGetQuoteById
} from '../../redux/ProjectsSlice';
import Grid from "@mui/material/Grid2";

import {
    Paper, Table,
    TableBody,
    TableCell,
    TableContainer,
    TableRow
} from "@mui/material";
import Moment from "moment/moment";
import Typography from "@mui/material/Typography";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AccordionDetails from "@mui/material/AccordionDetails";
import Box from "@mui/material/Box";
import {useTheme} from "@mui/material/styles";
import Button from "@mui/material/Button";
import {Link} from "react-router-dom";
import {getHistoriesById, selectHistoriesById} from "../../redux/HistoriesSlice";
import ProjectStatusChip from "../../Components/Projects/ProjectStatusChip";
import NotFound from "../../Components/Error/NotFound";
import GeneralError from "../../Components/Error/GeneralError";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import List from "@mui/material/List";
import ArrowRightRoundedIcon from '@mui/icons-material/ArrowRightRounded';
import ListItemIcon from "@mui/material/ListItemIcon";
import ProjectJobStatusView from "../../Components/Projects/ProjectJobStatusView";
import EditIcon from "@mui/icons-material/Edit";
import Breadcrumb from "../../Components/Navigation/Breadcrumb";
import LoadingOverlay from "../../Components/Loading/LoadingOverlay";
import MenuButton from "../../Components/Inputs/MenuButton";
import ChangeCircleIcon from '@mui/icons-material/ChangeCircle';
import LoadingWithStatusOverlay from "../../Components/Loading/LoadingWithStatusOverlay";

const ProjectDetails = () => {
    const { id } = useParams();
    const quoteFromRedux = useSelector(selectGetQuoteById)[id];
    const historyFromRedux = useSelector(selectHistoriesById)[id];
    const statusesLoadStatus = useSelector(getQuoteStatusesStatus);
    const navigate = useNavigate();
    const [quote, setQuote] = useState(quoteFromRedux);
    const [quoteHistory, setQuoteHistory] = useState(historyFromRedux);
    const [loadingStatus, setLoadingStatus] = useState("idle");
    const [loadingMessage, setLoadingMessage] = useState("");
    
    const [quoteError, setQuoteError] = useState(null);
    const [historyError, setHistoryError] = useState(null);
    const [pathMap, setPathMap] = useState({});

    const theme = useTheme();
    const dispatch = useDispatch();


    useEffect(() => {
        if (statusesLoadStatus === 'idle') {
            dispatch(getQuoteStatuses());
        }
    }, [dispatch, statusesLoadStatus]);
 
    useEffect(() => {
        document.title = 'Loading...';
        if (quoteFromRedux !== undefined) {
            setPathMap({ [id]: quoteFromRedux.friendlyId });

            setQuote(quoteFromRedux);
            document.title = `${quoteFromRedux.friendlyId} - CPC`;
        } else {
            dispatch(getQuoteById(id)).unwrap()
                .then((response) => {
                    setQuote(response)
                })
                .catch((error) => {
                    setQuoteError(error);
                    document.title = 'CPC';
                });
        }
    }, [dispatch, quote, id]);

    useEffect(() => {
        if (historyFromRedux !== undefined) {
            setQuoteHistory(historyFromRedux);
        } else {
            dispatch(getHistoriesById({id: id, entityType: "quote"})).unwrap()
                .then((response) => {
                    setQuoteHistory(response);
                })
                .catch((error) => {
                    setHistoryError(error);
                    document.title = 'CPC';
                });
        }
    }, [dispatch, quoteHistory, id]);

    if (quote && quoteError === null) {
            return (
                <div>
                    <Breadcrumb pathMap={pathMap} />
                    <Grid container
                          spacing={3}
                          justifyContent='space-between'
                    >
                        <Grid size={{ xs: 8, sm: 6, md: 5, lg: 4 }}
                              sx={{
                                  display: 'flex', 
                                  alignItems: 'center',
                                  order: 0 }}
                        >
                            <Typography variant={'h2'}>{quote.friendlyId}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12, md: 3, lg: 5}} sx={{
                            display: 'flex',
                            alignItems: 'center',
                            order: {xs: 30, md: 10}
                        }}>
                            <ProjectStatusChip quoteStatus={quote.quoteStatus} quoteId={id} />
                        </Grid>
                        <Grid size={{ xs: 4, md: 3, lg: 3 }}
                              sx={{
                                  order: {xs: 10, md: 20}
                              }}>
                            <Box sx={{
                                display: { xs: 'none', lg: 'flex' },
                                alignItems: 'center',
                                justifyContent: 'right'}}
                            >
                                <Link to="edit">
                                    <Button variant="contained" startIcon={<EditIcon />} className="edit-button">
                                        Edit
                                    </Button>
                                </Link>
                            </Box>
                            <Box sx={{
                                display: { xs: 'flex', lg: 'none' },
                                alignItems: 'center',
                                justifyContent: 'right'}}>
                                <MenuButton
                                    menuItems={(!!quote.jobConvertedAt || !!quote.jobNumber) ? [
                                        { text: 'Edit', icon: <EditIcon sx={{color: theme.palette.tertiary.secondary}} /> },
                                    ] : [
                                        { text: 'Convert to Job', icon: <ChangeCircleIcon sx={{color: theme.palette.secondary.main }} /> },
                                        { text: 'Edit', icon: <EditIcon sx={{color: theme.palette.tertiary.secondary}} /> }
                                    ]}
                                    onMenuItemClick={(item) => {
                                        if (item.text === 'Edit') {
                                            navigate('Edit');
                                        }
                                    }}
                                />
                            </Box>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ order: 40 }}>
                            <Paper sx={{
                                borderRadius: '4px !important',
                                overflow: 'hidden',
                            }}>
                                <Grid container spacing={0}>
                                    <Grid size={{ xs: 12 }}>
                                        <Typography variant="h6" sx={{ backgroundColor: theme.palette.primary.secondary, color: 'white', p: 1 }}>Project</Typography>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px',
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold'}}>Id</Typography>
                                            <Typography>{quote.friendlyId}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px',
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold'}}>Purchase Order Number</Typography>
                                            <Typography>{quote.purchaseOrderNumber ? quote.purchaseOrderNumber : "-"}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px',
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold'}}>Quote Number</Typography>
                                            <Typography>{quote.externalQuoteNumber ? quote.externalQuoteNumber : "-"}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px',
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold'}}>Job Number</Typography>
                                            <Typography>{quote.jobNumber ? quote.jobNumber : "-"}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12}}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px',
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold' }}>Client</Typography>
                                            <Typography>{quote.client.name}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12}}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px',
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold'}}>Description</Typography>
                                            <Typography sx={{textAlign: 'right'}}>{quote.description}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12}}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px',
                                                borderBottom: `1px solid ${theme.palette.divider}`
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold'}}>Created At</Typography>
                                            <Typography>{Moment(quote.createdAt).format('D MMM YYYY, h:mma')}</Typography>
                                        </Box>
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <Box
                                            sx={{
                                                display:'flex',
                                                justifyContent: 'space-between',
                                                padding: '10px'
                                            }}
                                        >
                                            <Typography sx={{ fontWeight: 'bold'}}>Created By</Typography>
                                            <Typography>{quote.createdBy}</Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Paper>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ order: 50 }}>
                            <ProjectJobStatusView quote={quote} />
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ order: {xs: 70, md: 60} }}>
                            <TableContainer component={Paper} >
                                <Table aria-label="quote history">
                                    <TableBody>
                                        <TableRow>
                                            <TableCell colSpan={3}
                                                       sx={{ backgroundColor:  theme.palette.primary.secondary,
                                                       p: 1}}
                                            >
                                                <Typography variant="h6" sx={{ color: 'white'}}>History</Typography>
                                            </TableCell>
                                        </TableRow>
                                        <TableRow >
                                            <TableCell>
                                                <Typography fontWeight="bold">Time</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="bold">Who</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography fontWeight="bold">Action(s)</Typography>
                                            </TableCell>
                                        </TableRow>
                                        {quoteHistory ? (
                                            quoteHistory.histories.map((history, index) => (
                                                <TableRow key={index}>
                                                    <TableCell
                                                        rowSpan={history.actions.count}
                                                    >
                                                        {Moment(history.occurredOn).format('D MMM YYYY, h:mma')}
                                                    </TableCell>
                                                    <TableCell rowSpan={history.actions.count}>{history.initiatedBy}</TableCell>
                                                    <TableCell rowSpan={history.actions.count}>
                                                        <ul>
                                                            {history.actions.map((action, index) => (
                                                                <li key={index}>{action.message}</li>
                                                            ))}
                                                        </ul>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        ) : (historyError === null ?
                                                <TableRow>
                                                    <TableCell colSpan={3}>Loading...</TableCell>
                                                </TableRow>
                                                :
                                                <TableRow>
                                                    <TableCell colSpan={3}>History couldn't be loaded at this time.</TableCell>
                                                </TableRow>
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }} sx={{ order: {xs: 60, md: 70} }}>
                            <Accordion defaultExpanded sx={{
                                borderRadius: '4px !important',
                                overflow: 'hidden',
                            }}>
                                <AccordionSummary
                                    expandIcon={<ExpandMoreIcon sx={{ color: 'white'}} />}
                                    sx={{ backgroundColor: theme.palette.primary.secondary,
                                        height: '45.3px', 
                                        minHeight: '45.3px',
                                        '&.Mui-expanded': {
                                            height: '45.3px',
                                            minHeight: '45.3px'
                                        },
                                    }}
                                >
                                    <Typography variant="h6" color={"white"}>Details</Typography>
                                </AccordionSummary>
                                <AccordionDetails sx={{ 
                                    padding: 0,
                                }}>
                                    <Table aria-label="quote item details" >
                                        <TableBody>
                                            {quote.quoteDetails?.map((detail, idx1) => {
                                                const isProductGroup = (detail.productGroupId !== undefined);
                                                return (<React.Fragment key={idx1}>
                                                        <TableRow sx={{ backgroundColor: theme.palette.primary.tertiary }}>
                                                            <TableCell colSpan={6}>
                                                                <Typography variant='h6' sx={{ fontWeight: 'bold'}}>{detail.quantity} x {isProductGroup
                                                                    ? detail.productGroupName
                                                                    : detail.productItemName}
                                                                </Typography>
                                                            </TableCell>
                                                        </TableRow>
                                                        {detail.quoteDetailFieldValues?.map((value, idx2) => {
                                                            return (
                                                                <React.Fragment key={idx2}>
                                                                    <TableRow >
                                                                        <TableCell colSpan={6} sx={{ borderBottom: '0px', py: 1}}>
                                                                            <List dense disablePadding>
                                                                                {value.fields.map((field, idx3) => (
                                                                                    <ListItem disablePadding key={`${value.id}-${field.id}-${idx3}`}>
                                                                                        <ListItemIcon sx={{ minWidth: '30px!important'}}>
                                                                                            <ArrowRightRoundedIcon />
                                                                                        </ListItemIcon>
                                                                                        <ListItemText primary={<><span style={{ fontWeight: 'bold' }}>{field.displayName}:</span> {`${field.value}${field.uom || ''}`}</>} />
                                                                                    </ListItem>
                                                                                ))}
                                                                            </List>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                        { isProductGroup && detail.categoryItems.map((item, idx4) => {
                                                            return (
                                                                <React.Fragment key={idx4}>
                                                                    <TableRow sx={{ backgroundColor: idx4 % 2 === 0 ? 'white' : theme.palette.lightGrey.main }}>
                                                                        <TableCell colSpan={6} sx={{ borderBottom: '0px', py: 1}}>
                                                                            <Typography sx={{ fontWeight: 'bold'}}>{item.quantity} x {item.productItemName}</Typography>
                                                                        </TableCell>
                                                                    </TableRow>
                                                                    {
                                                                        item.fields.length > 0 &&
                                                                        <TableRow sx={{ backgroundColor: idx4 % 2 === 0 ?  'white' : theme.palette.lightGrey.main }}>
                                                                            <TableCell colSpan={6} sx={{py: 1}}>
                                                                                <List dense disablePadding>
                                                                                    {item.fields.map((field, idx5) => (
                                                                                        <ListItem disablePadding key={`${item.id}-${field.id}-${idx5}`} sx={{ }}>
                                                                                            <ListItemIcon sx={{ minWidth: '30px!important'}}>
                                                                                                <ArrowRightRoundedIcon />
                                                                                            </ListItemIcon>
                                                                                            <ListItemText primary={<><span style={{ fontWeight: 'bold' }}>{field.displayName}:</span> {`${field.value}${field.uom || ''}`}</>} />
                                                                                        </ListItem>
                                                                                    ))}
                                                                                </List>
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    }
                                                                </React.Fragment>
                                                            )
                                                        })}
                                                    </React.Fragment>
                                                );
                                            })}
                                        </TableBody>
                                    </Table>
                                </AccordionDetails>
                            </Accordion>
                        </Grid>
                    </Grid>
                    <LoadingWithStatusOverlay
                        status={loadingStatus}
                        message={loadingMessage}
                        onComplete={() => {
                            setLoadingStatus("idle");
                            setLoadingMessage("");
                        }}
                    />
                </div>
            );
    } else if (quoteError !== null) {
        if(quoteError.status === 404) {
            return (
                <div>
                    <NotFound />
                </div>
            );
        } else {
            return (
                <div>
                    <GeneralError />
                </div>
            )
        }
    } else {
        return <LoadingOverlay />
    }
};

export default ProjectDetails;